interface Props {
  password: string;
}

/**
 * The rules the SERVER enforces (`server/src/utils/password.ts`
 * `validatePasswordComplexity`). The old heuristic scored length twice and
 * never required a symbol, so it could read "Strong" for a password the API
 * then rejected with a 400.
 */
const RULES: { label: string; test: (p: string) => boolean }[] = [
  { label: 'At least 8 characters', test: p => p.length >= 8 },
  { label: 'A lowercase letter', test: p => /[a-z]/.test(p) },
  { label: 'An uppercase letter', test: p => /[A-Z]/.test(p) },
  { label: 'A number', test: p => /[0-9]/.test(p) },
  { label: 'A symbol', test: p => /[^a-zA-Z0-9]/.test(p) },
];

export function PasswordStrengthMeter({ password }: Props) {
  if (!password) return null;

  const results = RULES.map(r => ({ ...r, pass: r.test(password) }));
  const met = results.filter(r => r.pass).length;
  const accepted = met === RULES.length;

  // Length beyond the minimum is real strength, but only once the password is
  // actually acceptable — otherwise it would report progress it doesn't have.
  const strong = accepted && password.length >= 12;
  const pct = (met / RULES.length) * 100;

  const barColor = !accepted
    ? met <= 2 ? 'bg-status-down' : 'bg-status-degraded'
    : strong ? 'bg-status-up' : 'bg-status-up/70';

  const summary = !accepted
    ? `${RULES.length - met} requirement${RULES.length - met > 1 ? 's' : ''} left`
    : strong ? 'Strong' : 'Meets requirements';

  return (
    <div className="mt-2">
      <div
        className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={RULES.length}
        aria-valuenow={met}
        aria-valuetext={summary}
        aria-label="Password requirements met"
      >
        <div className={`h-full ${barColor} transition-all duration-300 rounded-full`} style={{ width: `${pct}%` }} />
      </div>

      <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">{summary}</p>

      {!accepted && (
        <ul className="mt-1.5 space-y-0.5">
          {results.filter(r => !r.pass).map(r => (
            <li key={r.label} className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <span aria-hidden="true" className="w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-500 shrink-0" />
              {r.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
